import type { ApiErrorCode, Problem } from './models';

/**
 * Generic client-side error for a failed API call. Never carries a
 * credential value, a fingerprint preimage, or any technology name — the
 * message is always the es-ES formal string produced by {@link mapErrorCode}.
 */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly errorCode: ApiErrorCode | 'unknown';
  readonly traceId?: string;

  constructor(status: number, errorCode: ApiErrorCode | 'unknown', message: string, traceId?: string) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.errorCode = errorCode;
    this.traceId = traceId;
  }
}

/**
 * Maps the contract's closed error vocabulary to an es-ES formal,
 * user-facing message. Never mentions a technology, a gateway vendor, or
 * internal implementation detail — see the technology-concealment rule in
 * the engineering handbook.
 */
export function mapErrorCode(errorCode: ApiErrorCode | 'unknown'): string {
  switch (errorCode) {
    case 'validation_error':
      return 'Revise los datos introducidos: alguno de los campos no es válido.';
    case 'invalid_signature':
    case 'timestamp_out_of_range':
    case 'nonce_replayed':
      return 'No se ha podido verificar la solicitud. Inténtelo de nuevo.';
    case 'tenant_not_authorized':
      return 'No tiene autorización para consultar alguno de los establecimientos solicitados.';
    case 'policy_violation':
      return 'La operación no cumple la política configurada.';
    case 'idempotency_conflict':
    case 'idempotency_mismatch':
    case 'duplicate_order_reference':
    case 'conflict':
      return 'Ya existe una operación con esos datos.';
    case 'not_found':
      return 'No se ha encontrado el elemento solicitado.';
    case 'rate_limited':
      return 'Se han realizado demasiadas solicitudes. Espere unos instantes e inténtelo de nuevo.';
    case 'unauthenticated':
      return 'Su sesión ha caducado. Vuelva a acceder.';
    case 'access_denied':
      return 'No tiene permisos para realizar esta acción.';
    case 'internal_error':
    case 'unknown':
    default:
      return 'Servicio no disponible temporalmente. Inténtelo de nuevo más tarde.';
  }
}

/** Builds an {@link ApiRequestError} from a parsed `Problem` response body. */
export function toApiRequestError(status: number, problem: Partial<Problem> | undefined): ApiRequestError {
  const errorCode = (problem?.errorCode as ApiErrorCode) ?? 'unknown';
  return new ApiRequestError(status, errorCode, mapErrorCode(errorCode), problem?.traceId);
}
